/**
 * Personal info — editable name/phone, read-only email, avatar placeholder,
 * directory phone visibility. Wired: GET/PATCH /api/account/profile (falls
 * back to the session user if the GET isn't live yet). Port of
 * screens-account.jsx PersonalInfo.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { bool, str } from '@/components/gate/api';
import { InfoNote } from '@/components/gate/bits';
import { BottomBar, Segmented } from '@/features/community/bits';
import { goBack } from '@/features/account-admin/bits';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, Avatar, Button, Field, Input, Screen, Skeleton, TopBar } from '@/ui';

export default function PersonalInfoScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { user, refresh } = useSession();

  const [loading, setLoading] = React.useState(true);
  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phoneVisible, setPhoneVisible] = React.useState(false);
  const [nameError, setNameError] = React.useState<string | undefined>();
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      let profile: unknown = null;
      try {
        const body = await api.get<unknown>('/api/account/profile');
        profile = (body && typeof body === 'object' && (body as Record<string, unknown>).user) || body;
      } catch {
        profile = null; // endpoint not live yet — fall back to session user
      }
      if (cancelled) return;
      setName(str(profile, 'fullName', 'name') ?? user?.fullName ?? '');
      setPhone(str(profile, 'phone', 'phoneNumber') ?? user?.phone ?? '');
      setEmail(str(profile, 'email') ?? user?.email ?? '');
      setPhoneVisible(bool(profile, 'sharePhone', 'phoneVisible', 'directoryPhoneVisible', 'showPhone') ?? false);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!name.trim()) {
      setNameError(t('pinfo_name_error'));
      return;
    }
    setNameError(undefined);
    setSaving(true);
    setNotice(null);
    try {
      await api.patch<unknown>('/api/account/profile', {
        fullName: name.trim(), phone: phone.trim(), sharePhone: phoneVisible,
      });
      await refresh();
      goBack();
    } catch {
      setNotice(t('pinfo_save_error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('row_personal_info')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        {loading ? (
          <>
            <Skeleton h={80} w={80} r={40} style={{ alignSelf: 'center' }} />
            {[0, 1, 2].map((i) => <Skeleton key={i} h={68} />)}
          </>
        ) : (
          <>
            <View style={{ alignItems: 'center' }}>
              <Avatar name={name || 'Resident'} size={80} />
              <Button
                title={t('pinfo_change_photo')}
                kind="tertiary"
                style={{ marginTop: space.s2 }}
                onPress={() => setNotice(t('pinfo_photo_unavailable'))}
              />
            </View>
            <Field label={t('full_name')} error={nameError}>
              <Input value={name} onChangeText={setName} placeholder={t('full_name')} hasError={!!nameError} />
            </Field>
            <Field label={t('pinfo_phone')}>
              <Input value={phone} onChangeText={setPhone} placeholder="+91" keyboardType="phone-pad" />
            </Field>
            <Field label={t('email')} help={t('pinfo_email_help')}>
              <Input value={email} editable={false} style={{ backgroundColor: colors.canvas1, color: colors.textTertiary }} />
            </Field>
            <Field label={t('pinfo_show_phone')}>
              <Segmented
                options={[{ key: 'visible', label: t('pinfo_visible') }, { key: 'private', label: t('res_private') }]}
                value={phoneVisible ? 'visible' : 'private'}
                onChange={(k) => setPhoneVisible(k === 'visible')}
                grow
              />
            </Field>
            <Button
              title={t('cp_title')}
              kind="tertiary"
              onPress={() => router.push('/account/change-password')}
            />
            {notice ? <InfoNote icon="info">{notice}</InfoNote> : null}
            <AppText variant="bodySm" tone="tertiary" style={{ textAlign: 'center' }}>
              {t('pinfo_flat_note')}
            </AppText>
          </>
        )}
      </Screen>
      <BottomBar>
        <Button title={t('kp_save')} size="lg" loading={saving} disabled={loading} onPress={() => { void save(); }} />
      </BottomBar>
    </Screen>
  );
}
